/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { precedentsApi, chambersApi, mattersApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import HelpButton from "@/components/HelpButton";
import {
    Plus, MagnifyingGlass, TrashSimple, Scales, ThumbsUp, ThumbsDown, ArrowSquareOut,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const COURT_LABEL = {
    supreme_court: "Supreme Court",
    high_court: "High Court",
    tribunal: "Tribunal",
    district_court: "District Court",
    consumer_forum: "Consumer forum",
    rera: "RERA",
    nclt: "NCLT",
    nclat: "NCLAT",
    armed_forces: "AFT",
    foreign: "Foreign",
    other: "Other",
};

export default function Precedents() {
    const [list, setList] = useState([]);
    const [chambers, setChambers] = useState([]);
    const [matters, setMatters] = useState([]);
    const [q, setQ] = useState("");
    const [court, setCourt] = useState("all");
    const [fav, setFav] = useState("all");
    const [open, setOpen] = useState(false);

    const load = async () => {
        const params = {};
        if (q) params.q = q;
        if (court !== "all") params.court = court;
        if (fav === "favourable") params.favourable = true;
        if (fav === "adverse") params.favourable = false;
        setList(await precedentsApi.list(params).catch(() => []));
    };
    useEffect(() => { load(); }, [q, court, fav]);
    useEffect(() => {
        chambersApi.list().then(setChambers).catch(() => {});
        mattersApi.list().then(setMatters).catch(() => {});
    }, []);

    return (
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="precedents-page">
            <div className="rule-line pt-3 mb-6">
                <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-1">Knowledge</div>
                <div className="flex items-center gap-2">
                    <h1 className="font-serif text-4xl tracking-tight">Precedent Library</h1>
                    <HelpButton topic="precedents" testid="help-precedents" />
                </div>
                <p className="text-sm text-muted-foreground mt-1">Hand-curated case-law. Tag, score (favourable/adverse), and pin to matters.</p>
            </div>

            <div className="flex flex-wrap gap-2 mb-4 items-center" data-testid="precedents-toolbar">
                <div className="relative flex-1 min-w-[240px]">
                    <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search citation / title / ratio / tag / statute…" className="pl-8" data-testid="precedents-search" />
                </div>
                <Select value={court} onValueChange={setCourt}>
                    <SelectTrigger className="w-[180px]" data-testid="precedents-court-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All courts</SelectItem>
                        {Object.entries(COURT_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={fav} onValueChange={setFav}>
                    <SelectTrigger className="w-[150px]" data-testid="precedents-fav-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="favourable">Favourable</SelectItem>
                        <SelectItem value="adverse">Adverse</SelectItem>
                    </SelectContent>
                </Select>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild><Button data-testid="precedents-new-btn"><Plus size={14} /> Add precedent</Button></DialogTrigger>
                    <DialogContent className="max-w-2xl">
                        <DialogHeader>
                            <DialogTitle>New precedent</DialogTitle>
                            <DialogDescription>Save a binding or persuasive judgement for quick recall in future matters.</DialogDescription>
                        </DialogHeader>
                        <PrecedentForm chambers={chambers} matters={matters} onDone={() => { setOpen(false); load(); }} />
                    </DialogContent>
                </Dialog>
            </div>

            {!list.length ? (
                <div className="border border-dashed border-border rounded-md p-10 text-sm text-muted-foreground text-center" data-testid="precedents-empty">
                    <Scales size={32} weight="duotone" className="mx-auto mb-3" />
                    {q || court !== "all" || fav !== "all" ? "No precedents match these filters." : "Library is empty — add your first precedent."}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="precedents-list">
                    {list.map((p) => <PrecedentCard key={p.id} p={p} onChange={load} matters={matters} />)}
                </div>
            )}
        </div>
    );
}

function PrecedentCard({ p, onChange, matters }) {
    const [open, setOpen] = useState(false);
    const linked = matters.filter((m) => p.matter_ids?.includes(m.id));
    const remove = async () => {
        try { await precedentsApi.remove(p.id); toast.success("Deleted"); onChange?.(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    return (
        <div className="border border-border bg-white rounded-md p-3" data-testid={`precedent-${p.id}`}>
            <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        {p.favourable === true && <ThumbsUp size={14} className="text-emerald-700" weight="fill" />}
                        {p.favourable === false && <ThumbsDown size={14} className="text-destructive" weight="fill" />}
                        <span className="font-mono text-xs">{p.citation}</span>
                        <span className="chip chip-doc text-xs">{COURT_LABEL[p.court] || p.court}</span>
                        {p.year && <span className="text-xs text-muted-foreground">{p.year}</span>}
                        <span className="text-xs text-muted-foreground">used {p.usage_count}×</span>
                    </div>
                    {p.title && <div className="font-medium leading-tight">{p.title}</div>}
                    {p.holding && <div className="text-sm mt-1 text-muted-foreground">{p.holding}</div>}
                    {p.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                            {p.tags.map((t) => <span key={t} className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground border border-border rounded-sm px-1.5 py-0.5">{t}</span>)}
                        </div>
                    )}
                    {linked.length > 0 && (
                        <div className="text-xs text-muted-foreground mt-1">Pinned to: {linked.map((m) => m.title).join(" · ")}</div>
                    )}
                </div>
                <div className="flex flex-col gap-1 items-end">
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <button className="text-muted-foreground hover:text-foreground" data-testid={`precedent-view-${p.id}`}><ArrowSquareOut size={14} /></button>
                        </DialogTrigger>
                        <DialogContent className="max-w-3xl">
                            <DialogHeader>
                                <DialogTitle className="font-mono text-sm">{p.citation}</DialogTitle>
                                <DialogDescription>{p.title || COURT_LABEL[p.court]} · {p.bench || p.judge || "—"}</DialogDescription>
                            </DialogHeader>
                            <div className="space-y-2 text-sm max-h-[60vh] overflow-y-auto">
                                {p.facts && <div><div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Facts</div>{p.facts}</div>}
                                {p.holding && <div><div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Holding</div>{p.holding}</div>}
                                {p.ratio && <div><div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Ratio</div>{p.ratio}</div>}
                                {p.my_notes && <div><div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">My notes</div><div className="italic">{p.my_notes}</div></div>}
                                {p.url && <a href={p.url} target="_blank" rel="noreferrer" className="text-primary text-xs underline">View on source ↗</a>}
                            </div>
                        </DialogContent>
                    </Dialog>
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <button className="text-muted-foreground hover:text-destructive" data-testid={`precedent-delete-${p.id}`}><TrashSimple size={14} /></button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Delete this precedent?</AlertDialogTitle>
                                <AlertDialogDescription>Removes "{p.citation}" from the library.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={remove}>Delete</AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </div>
        </div>
    );
}

function PrecedentForm({ chambers, matters, onDone }) {
    const [v, setV] = useState({
        citation: "", title: "", court: "supreme_court", court_name: "", year: "",
        bench: "", judge: "", holding: "", ratio: "", facts: "",
        my_notes: "", tags: "", practice_area: "", statutes_referred: "",
        url: "", favourable: "neutral", matter_ids: [], chamber_id: "personal",
    });
    const submit = async () => {
        if (!v.citation) { toast.error("Citation is required"); return; }
        try {
            await precedentsApi.create({
                citation: v.citation, title: v.title, court: v.court, court_name: v.court_name,
                year: v.year ? parseInt(v.year, 10) : null,
                bench: v.bench, judge: v.judge, holding: v.holding, ratio: v.ratio, facts: v.facts,
                my_notes: v.my_notes, practice_area: v.practice_area, url: v.url,
                tags: v.tags.split(",").map((x) => x.trim()).filter(Boolean),
                statutes_referred: v.statutes_referred.split(",").map((x) => x.trim()).filter(Boolean),
                favourable: v.favourable === "neutral" ? null : v.favourable === "favourable",
                matter_ids: v.matter_ids,
                chamber_id: v.chamber_id === "personal" ? null : v.chamber_id,
            });
            toast.success("Precedent added");
            onDone?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    return (
        <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1" data-testid="precedent-form">
            <Input placeholder="Citation (e.g. (2023) 9 SCC 645)" value={v.citation} onChange={(e) => setV({ ...v, citation: e.target.value })} data-testid="prec-citation" />
            <Input placeholder="Case title (e.g. ABC v. XYZ)" value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} data-testid="prec-title" />
            <div className="grid grid-cols-3 gap-2">
                <Select value={v.court} onValueChange={(c) => setV({ ...v, court: c })}>
                    <SelectTrigger data-testid="prec-court"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(COURT_LABEL).map(([k, lbl]) => <SelectItem key={k} value={k}>{lbl}</SelectItem>)}</SelectContent>
                </Select>
                <Input placeholder="Court name" value={v.court_name} onChange={(e) => setV({ ...v, court_name: e.target.value })} />
                <Input type="number" placeholder="Year" value={v.year} onChange={(e) => setV({ ...v, year: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Bench" value={v.bench} onChange={(e) => setV({ ...v, bench: e.target.value })} />
                <Input placeholder="Authoring judge" value={v.judge} onChange={(e) => setV({ ...v, judge: e.target.value })} />
            </div>
            <Textarea rows={2} placeholder="Holding — 1 line ratio" value={v.holding} onChange={(e) => setV({ ...v, holding: e.target.value })} data-testid="prec-holding" />
            <Textarea rows={3} placeholder="Ratio decidendi — full" value={v.ratio} onChange={(e) => setV({ ...v, ratio: e.target.value })} />
            <Textarea rows={2} placeholder="Brief facts" value={v.facts} onChange={(e) => setV({ ...v, facts: e.target.value })} />
            <Textarea rows={2} placeholder="My notes / why I keep this" value={v.my_notes} onChange={(e) => setV({ ...v, my_notes: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Practice area" value={v.practice_area} onChange={(e) => setV({ ...v, practice_area: e.target.value })} />
                <Select value={v.favourable} onValueChange={(f) => setV({ ...v, favourable: f })}>
                    <SelectTrigger data-testid="prec-fav"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="neutral">Neutral / unrated</SelectItem>
                        <SelectItem value="favourable">Favourable to our side</SelectItem>
                        <SelectItem value="adverse">Adverse</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <Input placeholder="Statutes referred (comma separated)" value={v.statutes_referred} onChange={(e) => setV({ ...v, statutes_referred: e.target.value })} />
            <Input placeholder="Tags (comma separated)" value={v.tags} onChange={(e) => setV({ ...v, tags: e.target.value })} />
            <Input placeholder="URL (IndianKanoon link)" value={v.url} onChange={(e) => setV({ ...v, url: e.target.value })} />
            <Select value={v.chamber_id} onValueChange={(c) => setV({ ...v, chamber_id: c })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                    <SelectItem value="personal">Personal (only me)</SelectItem>
                    {chambers.map((c) => <SelectItem key={c.id} value={c.id}>Share with {c.name}</SelectItem>)}
                </SelectContent>
            </Select>
            <div className="flex justify-end pt-1"><Button onClick={submit} data-testid="prec-submit">Add precedent</Button></div>
        </div>
    );
}
